import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Save, X, Calendar, Newspaper } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewsManager = () => {
  const { token } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    published: true
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API}/news`);
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingNews) {
        // Update existing news
        await axios.put(
          `${API}/news/${editingNews.id}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      } else {
        // Create new news
        await axios.post(
          `${API}/news`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
      
      fetchNews();
      resetForm();
    } catch (error) {
      console.error('Error saving news:', error);
      alert('Error al guardar la noticia');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar esta noticia?')) return;
    
    try {
      await axios.delete(`${API}/news/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchNews();
    } catch (error) {
      console.error('Error deleting news:', error);
      alert('Error al eliminar la noticia');
    }
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      image_url: newsItem.image_url,
      published: newsItem.published
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      image_url: '',
      published: true
    });
    setEditingNews(null);
    setShowForm(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 rounded-full animate-spin" 
             style={{borderColor: '#D4AF37', borderTopColor: 'transparent'}}></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Gestión de Noticias</h1>
          <p className="text-gray-600">Administra las noticias del sitio web</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-6 py-3 text-white rounded-lg font-semibold transition-all shadow-lg hover:scale-105"
          style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
        >
          <Plus className="h-5 w-5" />
          Nueva Noticia
        </button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingNews ? 'Editar Noticia' : 'Nueva Noticia'}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">Título</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{'--tw-ring-color': '#D4AF37'}}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                  onBlur={(e) => e.target.style.boxShadow = 'none'}
                  placeholder="Título de la noticia"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">Contenido</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  required
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent resize-none"
                  style={{'--tw-ring-color': '#D4AF37'}}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                  onBlur={(e) => e.target.style.boxShadow = 'none'}
                  placeholder="Escribe el contenido de la noticia..."
                />
              </div>

              {/* Image URL */}
              <div>
                <label className="block text-gray-700 font-medium mb-2">URL de Imagen</label>
                <input
                  type="url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                  style={{'--tw-ring-color': '#D4AF37'}}
                  onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px rgba(212, 175, 55, 0.5)'}
                  onBlur={(e) => e.target.style.boxShadow = 'none'}
                  placeholder="https://ejemplo.com/imagen.jpg"
                />
              </div>

              {/* Published Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                  className="w-5 h-5 rounded"
                  style={{accentColor: '#D4AF37'}}
                />
                <label htmlFor="published" className="text-gray-700 font-medium">
                  Publicar noticia
                </label>
              </div>

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg font-semibold transition-all"
                  style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
                >
                  <Save className="h-5 w-5" />
                  {editingNews ? 'Actualizar' : 'Crear'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* News List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {news.length > 0 ? (
          news.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
            >
              {/* Image */}
              <div className="h-48 overflow-hidden">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-bold text-gray-800 line-clamp-2">{item.title}</h3>
                  {item.published ? (
                    <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium">
                      Publicado
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium">
                      Borrador
                    </span>
                  )}
                </div>
                <p className="text-gray-600 text-sm line-clamp-3 mb-4">{item.content}</p>
                
                {/* Date */}
                <div className="flex items-center gap-2 text-gray-500 text-xs mb-4">
                  <Calendar className="h-4 w-4" />
                  {new Date(item.created_at).toLocaleDateString('es-ES')}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 border-2 rounded-lg font-semibold transition-all hover:bg-gray-50"
                    style={{borderColor: '#D4AF37', color: '#D4AF37'}}
                  >
                    <Edit2 className="h-4 w-4" />
                    Editar
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="px-4 py-2 bg-red-50 text-red-600 rounded-lg font-semibold hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full text-center py-12">
            <Newspaper className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">No hay noticias</h3>
            <p className="text-gray-600 mb-4">Crea tu primera noticia para empezar</p>
            <button
              onClick={() => setShowForm(true)}
              className="inline-flex items-center gap-2 px-6 py-3 text-white rounded-lg font-semibold transition-all shadow-lg hover:scale-105"
              style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}
            >
              <Plus className="h-5 w-5" />
              Nueva Noticia
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsManager;
